// Return navigation: links back to the page the user came from
//
// A link into another page can carry the current location in the returnTo query param. The page
// it opens then shows "Go back to ..." (the origin) above its own "Go to ..." link (the parent view).
// BackNavLinks renders both, through SectionTitle and ContentPageHeader.
//
// When adding a page or a link:
//   - Links to another area carry the origin: withReturnTo() for string links, returnToQuery() for
//     route location objects. Links that only change the tab, or open a sub-form of the same page,
//     keep the current origin instead: pickReturnTo().
//   - Form pages declare `meta.form` in routes.ts and pages with tabs `meta.defaultTab`.
//   - A new origin route needs a text in renderReturnLinkText(), or it is shown as "Go back".

"use strict";

import type { LocationQuery, LocationQueryRaw, RouteLocation, RouteLocationNormalizedLoaded, Router } from "vue-router";
import { UNAUTHORIZED_ONLY_ROUTES } from "@/app-events-plugin";
import { RULEBOOK_AUTOMATION_QUERY } from "@/utils/rulebook-automation";

/**
 * Query parameter that stores the location to go back to
 */
export const RETURN_TO_QUERY = "returnTo";

/**
 * Query parameter that makes a form go back to its return target when it finishes,
 * instead of to the page it belongs to
 */
export const RETURN_WHEN_DONE_QUERY = "returnWhenDone";

/**
 * Query parameters that start an action when the page opens, so going back must not repeat them
 */
const ONE_SHOT_QUERY = [RULEBOOK_AUTOMATION_QUERY];

export type ReturnLink = {
    to: string;
    text: string;
};

type LocationIdentity = Pick<RouteLocation, "name" | "params" | "query" | "meta">;

/**
 * Adds the current location as return target to an internal link
 * @param to The internal link
 * @param current The current route
 * @returns The link including the return target
 */
export function withReturnTo(to: string, current: RouteLocationNormalizedLoaded): string {
    // A link to the current page (e.g. a list row pointing to the page that embeds the list)
    // keeps the current return target instead of pointing back to itself
    if (to.split("?")[0] === current.path) {
        return appendCurrentReturnTo(to, current);
    }

    const returnTo = getSourceReturnTo(current);
    return returnTo ? appendReturnTo(to, returnTo) : to;
}

/**
 * Gets the query that stores the current location as return target, for route location objects
 * @param current The current route
 * @returns The query to merge into a route location
 */
export function returnToQuery(current: RouteLocationNormalizedLoaded): LocationQueryRaw {
    const returnTo = getSourceReturnTo(current);
    return returnTo ? { [RETURN_TO_QUERY]: returnTo } : {};
}

/**
 * Keeps the current return target in a link to the same page (e.g. another tab of it)
 * @param router The router
 * @param current The current route
 * @param link The link
 * @returns The link, including the current return target if it points to the same page
 */
export function keepReturnTo(router: Router, current: RouteLocationNormalizedLoaded, link: string): string {
    if (!link || typeof current.query[RETURN_TO_QUERY] !== "string" || !isSamePage(router.resolve(link, current), current)) {
        return link;
    }

    return appendCurrentReturnTo(link, current);
}

/**
 * Keeps only the return target of a query, to preserve it when navigating inside the same page
 * @param query The current query
 * @returns The query with only the return target, if any
 */
export function pickReturnTo(query: LocationQuery): LocationQueryRaw {
    const value = query[RETURN_TO_QUERY];
    return typeof value === "string" ? { [RETURN_TO_QUERY]: value } : {};
}

/**
 * Resolves the link back to the page the user came from
 * @param router The router
 * @param current The current route
 * @param parentLink The default back link of the page
 * @param $t The translation function
 * @returns The return link, or null if there is no valid return target
 */
export function resolveReturnLink(
    router: Router,
    current: RouteLocationNormalizedLoaded,
    parentLink: string,
    $t: (t: string) => string,
): ReturnLink | null {
    const target = resolveReturnTarget(router, current);

    if (!target) {
        return null;
    }

    if (parentLink && isSameLocation(router.resolve(parentLink, current), target)) {
        return null;
    }

    return {
        to: withoutOneShotQuery(target),
        text: renderReturnLinkText(target, $t),
    };
}

/**
 * Resolves the page the user came from, if it is a valid page to go back to
 * @param router The router
 * @param current The current route
 * @returns The page to go back to, or null if there is no valid return target
 */
export function resolveReturnTarget(router: Router, current: RouteLocationNormalizedLoaded): RouteLocation | null {
    const value = current.query[RETURN_TO_QUERY];

    if (typeof value !== "string" || !isInternalPath(value)) {
        return null;
    }

    const target = router.resolve(value);

    // Unknown routes resolve to the unnamed catch-all route, and login, sign-up and form pages are not pages to go back to
    if (!target.name || UNAUTHORIZED_ONLY_ROUTES.includes(String(target.name)) || target.meta.form || isSamePage(target, current)) {
        return null;
    }

    return target;
}

/**
 * Gets the return target a link from the current page should carry
 */
function getSourceReturnTo(current: RouteLocationNormalizedLoaded): string | null {
    // Login and sign-up pages may carry tokens in their URL, so they are never copied
    if (UNAUTHORIZED_ONLY_ROUTES.includes(String(current.name || ""))) {
        return null;
    }

    // A form passes on its own return target instead
    if (current.meta.form) {
        const value = current.query[RETURN_TO_QUERY];
        return typeof value === "string" ? value : null;
    }

    return withoutOneShotQuery(current);
}

/**
 * Gets the path of a location without its one-shot flags (e.g. the one that starts the rulebook assistant)
 * @param location The location
 * @returns The path, query and hash of the location
 */
export function withoutOneShotQuery(location: Pick<RouteLocation, "query" | "fullPath">): string {
    if (!ONE_SHOT_QUERY.some((key) => key in location.query)) {
        return location.fullPath;
    }

    const url = new URL(location.fullPath, window.location.origin);
    ONE_SHOT_QUERY.forEach((key) => url.searchParams.delete(key));
    return url.pathname + url.search + url.hash;
}

function appendReturnTo(link: string, returnTo: string): string {
    const separator = link.includes("?") ? "&" : "?";
    return link + separator + RETURN_TO_QUERY + "=" + encodeURIComponent(returnTo);
}

function appendCurrentReturnTo(link: string, current: RouteLocationNormalizedLoaded): string {
    const value = current.query[RETURN_TO_QUERY];
    return typeof value === "string" ? appendReturnTo(link, value) : link;
}

/**
 * Checks that the value is a path of this application. It is parsed the way the browser does,
 * so values like "//host" cannot point to another site. Backslashes and control characters are
 * rejected because the router and the browser read them differently.
 */
function isInternalPath(value: string): boolean {
    if (!value.startsWith("/") || [...value].some((c) => c === "\\" || c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127)) {
        return false;
    }

    try {
        return new URL(value, window.location.origin).origin === window.location.origin;
    } catch {
        return false;
    }
}

function isSamePage(a: LocationIdentity, b: LocationIdentity): boolean {
    return a.name === b.name && JSON.stringify(a.params) === JSON.stringify(b.params);
}

function isSameLocation(a: LocationIdentity, b: LocationIdentity): boolean {
    return isSamePage(a, b) && serializeQuery(a) === serializeQuery(b);
}

function serializeQuery(location: LocationIdentity): string {
    // A missing tab means the default one, and the origin of a page does not make it a different page
    const query = { ...location.query, tab: getTab(location) };
    delete query[RETURN_TO_QUERY];
    const keys = Object.keys(query).sort();
    return JSON.stringify(keys.map((k) => [k, query[k]]));
}

function getTab(location: LocationIdentity): string {
    return String(location.query.tab || location.meta.defaultTab || "");
}

function renderReturnLinkText(target: LocationIdentity, $t: (t: string) => string): string {
    switch (target.name) {
        case "agreement":
        case "clearing-house-agreement":
            return $t("Go back to the agreement");
        case "transfer":
        case "clearing-house-transfer":
            return $t("Go back to the transfer");
        case "negotiation":
            return $t("Go back to the negotiation");
        case "dataset":
            return $t("Go back to the dataset");
        case "catalog":
            return $t("Go back to the catalog");
        case "data-app":
            return $t("Go back to the application");
        case "pipeline":
            return $t("Go back to the pipeline");
        case "execution":
            return $t("Go back to execution");
        case "analysis-result":
            return $t("Go back to the analysis result");
        case "clearing-house":
            // The clearing house page is a set of lists, so name the list (tab) to go back to
            switch (getTab(target)) {
                case "agreements":
                    return $t("Go back to clearing house agreements");
                case "transfers":
                    return $t("Go back to clearing house transfers");
                case "disputes":
                    return $t("Go back to dispute messages");
                default:
                    return $t("Go back");
            }
        case "broker-requests":
            return getTab(target) === "sent" ? $t("Go back to sent broker requests") : $t("Go back to received broker requests");
        case "attestation-requests":
            switch (getTab(target)) {
                case "sent":
                    return $t("Go back to sent attestation requests");
                case "received":
                    return $t("Go back to received attestation requests");
                default:
                    return $t("Go back to attestation requests");
            }
        case "app-marketplace":
            return $t("Go back to app marketplace");
        case "app-marketplace-moderation-request":
            return $t("Go back to the moderation request");
        case "app-marketplace-publication":
            return $t("Go back to the publication");
        case "app-marketplace-listing":
            return $t("Go back to the listing");
        case "data-source":
            return $t("Go back to the data source");
        case "data-sink":
            return $t("Go back to the data sink");
        case "negotiation-automation-policy":
            return $t("Go back to the policy");
        case "broker-request":
        case "provider-request":
            return $t("Go back to the broker request");
        case "attestation-request-sent":
        case "attestation-request-received":
            return $t("Go back to the attestation request");
        case "admin-data-space":
            return $t("Go back to data space configuration");
        case "notifications":
            return $t("Go back to notifications");
        case "transfers":
            return $t("Go back to transfers");
        case "agreements":
            return $t("Go back to agreements");
        case "negotiations":
            return $t("Go back to negotiations");
        case "datasets":
            return $t("Go back to datasets");
        case "catalogs":
            return $t("Go back to catalogs");
        case "data-sources":
            return $t("Go back to data sources");
        case "data-sinks":
            return $t("Go back to data sinks");
        case "data-apps":
            return $t("Go back to data applications");
        case "pipelines":
            return $t("Go back to pipelines");
        case "negotiation-automation-policies":
            return $t("Go back to policies");
        default:
            return $t("Go back");
    }
}
